import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { Menu, X, Atom, GraduationCap, Phone } from 'lucide-react';
import StemBackgroundDecorations from './StemBackgroundDecorations';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/courses', label: 'Courses' },
    { path: '/complimentary-session', label: 'Complimentary Session' },
    { path: '/about', label: 'About' },
    { path: '/results', label: 'Results' },
    { path: '/blog', label: 'Blog' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-deep-blue/95 shadow-lg' : 'bg-transparent'
      }`}
      style={scrolled ? { WebkitBackdropFilter: 'blur(16px)', backdropFilter: 'blur(16px)' } : {}}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 rounded-lg bg-electric-cyan/20 flex items-center justify-center">
              <Atom className="w-6 h-6 text-electric-cyan" />
            </div>
            <span className="font-outfit font-bold text-xl text-white">
              Physics<span className="text-electric-cyan">Co</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-electric-cyan'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/student/login"
              data-testid="nav-login-btn"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Login
            </Link>
            <Link
              to="/register"
              data-testid="nav-register-btn"
              className="btn-primary text-sm"
            >
              Register Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-deep-navy border-t border-surface-navy"
          >
            <div className="container mx-auto px-4 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-2 text-sm font-medium ${
                    location.pathname === link.path
                      ? 'text-electric-cyan'
                      : 'text-slate-300'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/student/login"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-sm font-medium text-slate-300"
              >
                Login
              </Link>
              <Link
                to="/register"
                onClick={() => setIsOpen(false)}
                className="btn-primary text-sm block text-center"
              >
                Register Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/site-sections/footer`).then(res => setFooterData(res.data));
    axios.get(`${API}/public/site-sections/contact_info`).then(res => setContactData(res.data));
  }, []);

  return (
    <footer className="bg-deep-navy border-t border-surface-navy py-16">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-electric-cyan/20 flex items-center justify-center">
                <Atom className="w-6 h-6 text-electric-cyan" />
              </div>
              <span className="font-outfit font-bold text-xl text-white">
                Physics<span className="text-electric-cyan">Co</span>
              </span>
            </Link>
            <p className="text-slate-400 text-sm">
              {footerData?.content?.tagline || 'Premium STEM Academy Singapore'}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[{ path: '/courses', label: 'Courses' }, { path: '/about', label: 'About' }, { path: '/results', label: 'Results' }, { path: '/blog', label: 'Blog' }].map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-slate-400 hover:text-electric-cyan text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-2">
              {[{ path: '/faq', label: 'FAQ' }, { path: '/contact', label: 'Contact' }, { path: '/terms', label: 'Terms' }, { path: '/privacy', label: 'Privacy' }].map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-slate-400 hover:text-electric-cyan text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-outfit font-semibold text-white mb-4">Contact</h4>
            <div className="space-y-2 text-slate-400 text-sm">
              <p>{contactData?.content?.email || 'contact@physicsco.sg'}</p>
              <p>{contactData?.content?.phone || '+65 8888 8888'}</p>
              <p>{contactData?.content?.hours || 'Mon-Sat: 9AM - 9PM'}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-surface-navy mt-12 pt-8 text-center text-slate-500 text-sm">
          {footerData?.content?.copyright || '© 2024 PhysicsCo. All rights reserved.'}
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  
  // Determine decoration variant based on current page
  const getDecorationVariant = () => {
    const path = location.pathname;
    if (path === '/') return 'hero';
    if (path === '/courses' || path.startsWith('/courses/')) return 'courses';
    if (path === '/about') return 'about';
    if (path === '/results') return 'results';
    if (path === '/register' || path === '/payment') return 'minimal';
    return 'default';
  };

  return (
    <div className="min-h-screen bg-deep-blue grid-bg relative">
      <StemBackgroundDecorations variant={getDecorationVariant()} />
      <Navbar />
      <main className="pt-20 relative z-10">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
